import { type Ref } from 'vue'
import type {
  CropRegion,
  CroppedResult,
  DocumentAdjustments,
  EditMode,
  ImageDisplayParams,
  SelectedRegionId
} from '../types'

export function useCanvasRenderer(
  canvas: Ref<HTMLCanvasElement | null>,
  ctx: Ref<CanvasRenderingContext2D | null>,
  image: Ref<HTMLImageElement | null>,
  editMode: Ref<EditMode>,
  cropRegions: Ref<CropRegion[]>,
  selectedRegionId: Ref<string | null>,
  croppedResults: Ref<CroppedResult[]>,
  selectedCroppedRegionId: Ref<SelectedRegionId>,
  rotation: Ref<number>,
  adjustments: Ref<Record<string, DocumentAdjustments>>,
  imageDisplayParams: Ref<ImageDisplayParams | null>,
  trimAmounts: Ref<Record<string, number>>,
  t: (key: string) => string
) {
  // Store the base display scale calculated from the original image
  // This ensures consistent sizing across rotations
  let baseDisplayScale: number | null = null
  function draw() {
    if (!canvas.value || !ctx.value || !image.value) {
      return
    }

    // Show cropped results in transform modes (no crop overlay)
    if (
      croppedResults.value.length > 0 &&
      (editMode.value === 'rotate' || editMode.value === 'adjust' || editMode.value === 'trim')
    ) {
      if (selectedCroppedRegionId.value === 'all') {
        updateMainCanvasWithAllCrops()
      } else if (selectedCroppedRegionId.value) {
        drawSelectedCroppedRegion()
      } else {
        updateMainCanvasWithAllCrops()
      }
      return
    }

    // Show cropped results in crop mode if they exist
    if (croppedResults.value.length > 0 && editMode.value === 'crop') {
      if (selectedCroppedRegionId.value === 'all') {
        updateMainCanvasWithAllCrops()
      } else if (selectedCroppedRegionId.value) {
        drawSelectedCroppedRegion()
      } else {
        updateMainCanvasWithAllCrops()
      }
      return
    }

    // Set canvas buffer to source image's native resolution
    // This prevents downscaling blur by maintaining high-res buffer
    const sourceWidth = image.value.naturalWidth || image.value.width
    const sourceHeight = image.value.naturalHeight || image.value.height

    // Calculate rotation
    const rotationRad = (rotation.value * Math.PI) / 180
    const isRotated90or270 = Math.abs(rotation.value % 180) === 90

    // Calculate the bounding box of the rotated image at native resolution
    let boundingWidth, boundingHeight
    if (isRotated90or270) {
      // 90° or 270° rotation swaps dimensions
      boundingWidth = sourceHeight
      boundingHeight = sourceWidth
    } else {
      // 0° or 180° rotation keeps dimensions
      boundingWidth = sourceWidth
      boundingHeight = sourceHeight
    }

    // Set canvas buffer to rotated bounding box size (native resolution)
    canvas.value.width = boundingWidth
    canvas.value.height = boundingHeight

    // Clear and reset
    ctx.value.clearRect(0, 0, canvas.value.width, canvas.value.height)
    ctx.value.setTransform(1, 0, 0, 1, 0, 0)
    ctx.value.globalAlpha = 1

    // Disable image smoothing for crisp rendering
    ctx.value.imageSmoothingEnabled = false

    // Apply rotation transformation at center
    ctx.value.save()
    ctx.value.translate(canvas.value.width / 2, canvas.value.height / 2)
    ctx.value.rotate(rotationRad)
    ctx.value.translate(-sourceWidth / 2, -sourceHeight / 2)

    // Apply trim to original image if specified
    const originalTrimAmount = trimAmounts.value['original'] || 0
    if (originalTrimAmount > 0) {
      // Calculate trim amounts at native resolution
      const trimAmountX = (sourceWidth * originalTrimAmount) / 200
      const trimAmountY = (sourceHeight * originalTrimAmount) / 200

      // Calculate source dimensions after trim
      const sourceX = trimAmountX
      const sourceY = trimAmountY
      const trimmedWidth = sourceWidth - trimAmountX * 2
      const trimmedHeight = sourceHeight - trimAmountY * 2

      // Draw the trimmed portion at native resolution
      ctx.value.drawImage(
        image.value,
        sourceX,
        sourceY,
        trimmedWidth,
        trimmedHeight,
        0,
        0,
        sourceWidth,
        sourceHeight
      )
    } else {
      // Draw the full image at native resolution (1:1 pixel mapping)
      ctx.value.drawImage(image.value, 0, 0, sourceWidth, sourceHeight)
    }

    ctx.value.restore()

    // Store display parameters for coordinate conversion (at native resolution)
    imageDisplayParams.value = {
      offsetX: 0,
      offsetY: 0,
      drawWidth: sourceWidth,
      drawHeight: sourceHeight,
      scale: 1.0, // No scaling - 1:1 mapping
      rotation: rotation.value,
      canvasWidth: canvas.value.width,
      canvasHeight: canvas.value.height
    }

    // Apply document adjustments
    const allAdjustments = adjustments.value['all']
    if (
      allAdjustments &&
      (Number(allAdjustments.brightness) !== 0 ||
        Number(allAdjustments.contrast) !== 0 ||
        Number(allAdjustments.gamma) !== 0 ||
        Number(allAdjustments.sharpness) !== 0 ||
        Number(allAdjustments.saturation) !== 0)
    ) {
      applyDocumentAdjustments()
    }

    // NOW scale the canvas DISPLAY via CSS to fit container
    // Maintain consistent scale across rotations by calculating from original dimensions
    const containerElement = canvas.value.parentElement
    if (containerElement) {
      const containerWidth = containerElement.clientWidth
      const containerHeight = containerElement.clientHeight

      // Only calculate scale if container has valid dimensions
      if (containerWidth > 100 && containerHeight > 100) {
        // Calculate base scale from ORIGINAL image dimensions (not rotated)
        // This ensures consistent pixel density regardless of rotation
        if (baseDisplayScale === null) {
          const originalAspectRatio = sourceWidth / sourceHeight
          const containerAspectRatio = containerWidth / containerHeight

          if (originalAspectRatio > containerAspectRatio) {
            // Original image is wider - fit to container width
            baseDisplayScale = (containerWidth * 0.95) / sourceWidth
          } else {
            // Original image is taller - fit to container height
            baseDisplayScale = (containerHeight * 0.95) / sourceHeight
          }
        }

        // Apply the base scale to current (potentially rotated) canvas dimensions
        const displayWidth = canvas.value.width * baseDisplayScale
        const displayHeight = canvas.value.height * baseDisplayScale

        // Apply CSS scaling (browser handles this smoothly)
        canvas.value.style.width = Math.floor(displayWidth) + 'px'
        canvas.value.style.height = Math.floor(displayHeight) + 'px'
        canvas.value.style.imageRendering = 'crisp-edges'
      }
    }

    // Draw crop overlay only in crop mode when no cropped results exist
    // NOTE: Crop overlay coordinates need to be scaled if they were created
    // at a different canvas size - you may need to adjust this logic
    if (editMode.value === 'crop' && croppedResults.value.length === 0) {
      drawCropOverlay()
    }
  }

  function drawSelectedCroppedRegion() {
    if (!canvas.value || !ctx.value) return

    // Find the selected cropped result
    const selectedResult = croppedResults.value.find(
      (result) => result.regionId === selectedCroppedRegionId.value
    )

    if (!selectedResult) return

    // Clear canvas and reset transformations
    ctx.value.clearRect(0, 0, canvas.value.width, canvas.value.height)
    ctx.value.setTransform(1, 0, 0, 1, 0, 0)
    ctx.value.globalAlpha = 1

    // Load and display the selected cropped image
    const img = new Image()
    img.onload = () => {
      if (!canvas.value || !ctx.value) return

      // Calculate how to fit the image in the canvas
      const imgAspectRatio = img.width / img.height
      const canvasAspectRatio = canvas.value.width / canvas.value.height

      let drawWidth, drawHeight, offsetX, offsetY

      if (imgAspectRatio > canvasAspectRatio) {
        // Image is wider - fit to width
        drawWidth = canvas.value.width
        drawHeight = canvas.value.width / imgAspectRatio
        offsetX = 0
        offsetY = (canvas.value.height - drawHeight) / 2
      } else {
        // Image is taller - fit to height
        drawHeight = canvas.value.height
        drawWidth = canvas.value.height * imgAspectRatio
        offsetX = (canvas.value.width - drawWidth) / 2
        offsetY = 0
      }

      // Draw the cropped image
      ctx.value.drawImage(img, offsetX, offsetY, drawWidth, drawHeight)
    }
    img.src = selectedResult.imageData
  }

  function updateMainCanvasWithAllCrops() {
    if (!canvas.value || !ctx.value || croppedResults.value.length === 0) return

    // Clear the main canvas and reset transformations
    ctx.value.clearRect(0, 0, canvas.value.width, canvas.value.height)
    ctx.value.setTransform(1, 0, 0, 1, 0, 0)
    ctx.value.globalAlpha = 1

    // Calculate grid layout
    const numResults = croppedResults.value.length
    const cols = Math.ceil(Math.sqrt(numResults))
    const rows = Math.ceil(numResults / cols)

    const cellWidth = canvas.value.width / cols
    const cellHeight = canvas.value.height / rows
    const padding = 10

    // Load and draw each cropped result
    croppedResults.value.forEach((result, index) => {
      const img = new Image()
      img.onload = () => {
        const col = index % cols
        const row = Math.floor(index / cols)

        const cellX = col * cellWidth + padding
        const cellY = row * cellHeight + padding
        const availableWidth = cellWidth - padding * 2
        const availableHeight = cellHeight - padding * 2

        // Calculate scaling to fit image in cell
        const imgAspectRatio = img.width / img.height
        const cellAspectRatio = availableWidth / availableHeight

        let drawWidth, drawHeight, offsetX, offsetY

        if (imgAspectRatio > cellAspectRatio) {
          // Image is wider - fit to width
          drawWidth = availableWidth
          drawHeight = availableWidth / imgAspectRatio
          offsetX = cellX
          offsetY = cellY + (availableHeight - drawHeight) / 2
        } else {
          // Image is taller - fit to height
          drawHeight = availableHeight
          drawWidth = availableHeight * imgAspectRatio
          offsetX = cellX + (availableWidth - drawWidth) / 2
          offsetY = cellY
        }

        // Draw the cropped image
        ctx.value!.drawImage(img, offsetX, offsetY, drawWidth, drawHeight)

        // Draw border around the cell
        ctx.value!.strokeStyle = '#e5e7eb'
        ctx.value!.lineWidth = 1
        ctx.value!.strokeRect(
          cellX - padding / 2,
          cellY - padding / 2,
          availableWidth + padding,
          availableHeight + padding
        )

        // Draw label with bounds checking
        ctx.value!.fillStyle = '#374151'
        ctx.value!.font = '12px sans-serif'
        const regionIndex = cropRegions.value.findIndex((region) => region.id === result.regionId)
        const displayName = regionIndex !== -1 ? `${t('page')} ${regionIndex + 1}` : result.regionId

        // Ensure label stays within canvas bounds
        const labelY = Math.max(15, cellY - 5) // At least 15px from top
        ctx.value!.fillText(displayName, cellX, labelY)
      }
      img.src = result.imageData
    })
  }

  function drawCropOverlay() {
    if (!canvas.value || !ctx.value) return

    // Only show crop regions in crop mode
    if (editMode.value !== 'crop') return

    // Calculate scale factor: canvas buffer size vs CSS display size
    // Crop regions are in CSS display coordinates, but canvas buffer is now at native resolution
    const cssWidth = parseFloat(canvas.value.style.width) || canvas.value.clientWidth
    const cssHeight = parseFloat(canvas.value.style.height) || canvas.value.clientHeight

    // Scale from CSS display coordinates to canvas buffer coordinates
    const scaleX = canvas.value.width / cssWidth
    const scaleY = canvas.value.height / cssHeight

    // Draw all crop regions with design-matching styling
    cropRegions.value.forEach((region, index) => {
      const isActive = region.id === selectedRegionId.value

      // Colors matching the design
      const strokeColor = 'hsl(271, 81%, 56%)'
      const fillColor = isActive ? 'hsla(271, 81%, 56%, 0.15)' : 'hsla(271, 81%, 56%, 0.08)'

      // Draw dashed border with scaled coordinates
      ctx.value!.strokeStyle = strokeColor
      ctx.value!.lineWidth = (isActive ? 3 : 2) * scaleX // Scale line width too
      ctx.value!.setLineDash([8 * scaleX, 4 * scaleX]) // Scale dash pattern
      ctx.value!.beginPath()

      // Scale the first vertex
      ctx.value!.moveTo(region.vertices[0].x * scaleX, region.vertices[0].y * scaleY)

      // Scale all other vertices
      for (let i = 1; i < region.vertices.length; i++) {
        ctx.value!.lineTo(region.vertices[i].x * scaleX, region.vertices[i].y * scaleY)
      }

      ctx.value!.closePath()
      ctx.value!.stroke()
      ctx.value!.setLineDash([])

      // Fill
      ctx.value!.fillStyle = fillColor
      ctx.value!.fill()

      // Draw corner handles with scaled positions
      region.vertices.forEach((vertex) => {
        const handleRadius = (isActive ? 8 : 6) * scaleX // Scale handle size
        const scaledX = vertex.x * scaleX
        const scaledY = vertex.y * scaleY

        ctx.value!.fillStyle = 'hsla(271, 81%, 56%, 0.7)'
        ctx.value!.strokeStyle = 'white'
        ctx.value!.lineWidth = (isActive ? 4 : 3) * scaleX

        ctx.value!.beginPath()
        ctx.value!.arc(scaledX, scaledY, handleRadius, 0, Math.PI * 2)
        ctx.value!.fill()
        ctx.value!.stroke()
      })

      // Draw edge handles with scaled positions
      if (isActive) {
        for (let i = 0; i < region.vertices.length; i++) {
          const currentVertex = region.vertices[i]
          const nextVertex = region.vertices[(i + 1) % region.vertices.length]

          // Scale the midpoint calculation
          const midX = ((currentVertex.x + nextVertex.x) / 2) * scaleX
          const midY = ((currentVertex.y + nextVertex.y) / 2) * scaleY

          // Edge direction (already normalized, doesn't need scaling)
          const edgeX = nextVertex.x - currentVertex.x
          const edgeY = nextVertex.y - currentVertex.y
          const edgeLength = Math.sqrt(edgeX * edgeX + edgeY * edgeY)
          const normalizedX = edgeX / edgeLength
          const normalizedY = edgeY / edgeLength

          const pillWidth = 20 * scaleX // Scale pill dimensions
          const pillHeight = 8 * scaleX
          const pillRadius = pillHeight / 2

          ctx.value!.fillStyle = 'white'
          ctx.value!.strokeStyle = 'hsla(271, 81%, 56%, 0.7)'
          ctx.value!.lineWidth = 3 * scaleX

          ctx.value!.save()
          ctx.value!.translate(midX, midY)
          ctx.value!.rotate(Math.atan2(normalizedY, normalizedX))

          ctx.value!.beginPath()
          if (typeof ctx.value!.roundRect === 'function') {
            ctx.value!.roundRect(-pillWidth / 2, -pillHeight / 2, pillWidth, pillHeight, pillRadius)
          } else {
            const x = -pillWidth / 2
            const y = -pillHeight / 2
            ctx.value!.moveTo(x + pillRadius, y)
            ctx.value!.lineTo(x + pillWidth - pillRadius, y)
            ctx.value!.arc(
              x + pillWidth - pillRadius,
              y + pillRadius,
              pillRadius,
              -Math.PI / 2,
              Math.PI / 2
            )
            ctx.value!.lineTo(x + pillRadius, y + pillHeight)
            ctx.value!.arc(x + pillRadius, y + pillRadius, pillRadius, Math.PI / 2, -Math.PI / 2)
            ctx.value!.closePath()
          }
          ctx.value!.fill()
          ctx.value!.stroke()
          ctx.value!.restore()
        }
      }

      // Draw label with scaled position
      if (isActive) {
        ctx.value!.fillStyle = 'hsl(271, 81%, 56%)'
        ctx.value!.font = `bold ${14 * scaleX}px Arial` // Scale font size

        const scaledX = region.vertices[0].x * scaleX
        const scaledY = region.vertices[0].y * scaleY

        const labelX = Math.max(5 * scaleX, Math.min(scaledX, canvas.value!.width - 80 * scaleX))
        const labelY = Math.max(20 * scaleY, scaledY - 15 * scaleY)

        ctx.value!.fillText(`${t('page')} ${index + 1}`, labelX, labelY)
      }
    })
  }

  function applyDocumentAdjustments() {
    if (!canvas.value || !ctx.value) return

    const imageData = ctx.value.getImageData(0, 0, canvas.value.width, canvas.value.height)
    const data = imageData.data

    // Calculate adjustment factors (ensure numeric values) - use 'all' adjustments
    const allAdjustments = adjustments.value['all'] || {
      brightness: 0,
      contrast: 0,
      gamma: 0,
      sharpness: 0,
      saturation: 0
    }
    const brightnessFactor = Number(allAdjustments.brightness) // -50 to +50
    const contrastFactor = 1 + Number(allAdjustments.contrast) / 100 // 0.5 to 1.5
    const gammaValue = 1 + Number(allAdjustments.gamma) / 100 // 0.8 to 1.2
    const gammaCorrection = 1 / gammaValue
    const saturationFactor = 1 + Number(allAdjustments.saturation) / 100 // 0.5 to 1.5

    for (let i = 0; i < data.length; i += 4) {
      let r = data[i]
      let g = data[i + 1]
      let b = data[i + 2]

      // Apply brightness (additive)
      r += brightnessFactor
      g += brightnessFactor
      b += brightnessFactor

      // Apply contrast (around midpoint)
      r = (r - 128) * contrastFactor + 128
      g = (g - 128) * contrastFactor + 128
      b = (b - 128) * contrastFactor + 128

      // Apply gamma correction (for document readability)
      if (Number(allAdjustments.gamma) !== 0) {
        r = 255 * Math.pow(r / 255, gammaCorrection)
        g = 255 * Math.pow(g / 255, gammaCorrection)
        b = 255 * Math.pow(b / 255, gammaCorrection)
      }

      // Apply saturation adjustment
      if (Number(allAdjustments.saturation) !== 0) {
        // Convert RGB to HSL for saturation adjustment
        const rNorm = r / 255
        const gNorm = g / 255
        const bNorm = b / 255

        const max = Math.max(rNorm, gNorm, bNorm)
        const min = Math.min(rNorm, gNorm, bNorm)
        const lightness = (max + min) / 2

        if (max !== min) {
          const delta = max - min
          const saturation = lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min)

          // Adjust saturation
          const newSaturation = Math.max(0, Math.min(1, saturation * saturationFactor))

          // Convert back to RGB
          const chroma = (1 - Math.abs(2 * lightness - 1)) * newSaturation
          const huePrime =
            max === rNorm
              ? ((gNorm - bNorm) / delta) % 6
              : max === gNorm
                ? (bNorm - rNorm) / delta + 2
                : (rNorm - gNorm) / delta + 4
          const x = chroma * (1 - Math.abs((huePrime % 2) - 1))
          const m = lightness - chroma / 2

          let rNew, gNew, bNew
          if (huePrime >= 0 && huePrime < 1) {
            rNew = chroma
            gNew = x
            bNew = 0
          } else if (huePrime >= 1 && huePrime < 2) {
            rNew = x
            gNew = chroma
            bNew = 0
          } else if (huePrime >= 2 && huePrime < 3) {
            rNew = 0
            gNew = chroma
            bNew = x
          } else if (huePrime >= 3 && huePrime < 4) {
            rNew = 0
            gNew = x
            bNew = chroma
          } else if (huePrime >= 4 && huePrime < 5) {
            rNew = x
            gNew = 0
            bNew = chroma
          } else {
            rNew = chroma
            gNew = 0
            bNew = x
          }

          r = (rNew + m) * 255
          g = (gNew + m) * 255
          b = (bNew + m) * 255
        }
      }

      // Clamp values to 0-255
      data[i] = Math.max(0, Math.min(255, r))
      data[i + 1] = Math.max(0, Math.min(255, g))
      data[i + 2] = Math.max(0, Math.min(255, b))
    }

    ctx.value.putImageData(imageData, 0, 0)

    // Apply sharpness if needed (unsharp mask for text clarity)
    if (Number(allAdjustments.sharpness) > 0) {
      applySharpness()
    }
  }

  function applySharpness() {
    const allAdjustments = adjustments.value['all'] || {
      brightness: 0,
      contrast: 0,
      gamma: 0,
      sharpness: 0,
      saturation: 0
    }
    if (!canvas.value || !ctx.value || Number(allAdjustments.sharpness) === 0) return

    // Simple unsharp mask implementation for text clarity
    const imageData = ctx.value.getImageData(0, 0, canvas.value.width, canvas.value.height)
    const data = imageData.data
    const width = canvas.value.width
    const height = canvas.value.height
    const strength = Number(allAdjustments.sharpness) / 100 // 0 to 1

    // Create a copy for the blur calculation
    const originalData = new Uint8ClampedArray(data)

    // Apply a simple sharpening kernel
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const idx = (y * width + x) * 4

        for (let c = 0; c < 3; c++) {
          // RGB channels
          const center = originalData[idx + c]
          const surrounding =
            (originalData[((y - 1) * width + x) * 4 + c] +
              originalData[((y + 1) * width + x) * 4 + c] +
              originalData[(y * width + (x - 1)) * 4 + c] +
              originalData[(y * width + (x + 1)) * 4 + c]) /
            4

          const sharpened = center + strength * (center - surrounding)
          data[idx + c] = Math.max(0, Math.min(255, sharpened))
        }
      }
    }

    ctx.value.putImageData(imageData, 0, 0)
  }

  function resetDisplayScale() {
    baseDisplayScale = null
  }

  return {
    draw,
    drawSelectedCroppedRegion,
    updateMainCanvasWithAllCrops,
    drawCropOverlay,
    applyDocumentAdjustments,
    applySharpness,
    resetDisplayScale
  }
}
